import { ROLES } from './roles';

/**
 * Login module catalog — department entry points on the login hub.
 * Order: left column (first half), right column (second half).
 * Shared logins: Fleet Entry+Ops (`ops`), MRO+Planning (`mro`), Client+External Operator (`client`).
 */
export const LOGIN_DEPARTMENTS = [
  {
    slug: 'accountable-manager',
    name: 'Accountable Manager',
    description: 'Accountable Manager dashboard login',
    tagline: 'Leadership. Compliance. Assurance.',
    color: '#2B7DE1',
    roles: [ROLES.ACCOUNTABLE_MANAGER]
  },
  {
    slug: 'crew',
    name: 'Crew',
    description: 'Crew / Pilot login',
    tagline: 'People. Performance. Progress.',
    color: '#2EB8A8',
    roles: [ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT]
  },
  {
    slug: 'ops',
    name: 'Fleet Entry & Operations',
    description: 'Fleet Entry, Operations Board, and Crew Duty',
    tagline: 'Operate. Optimize. Outperform.',
    color: '#E040A8',
    roles: [ROLES.SALES, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE]
  },
  {
    slug: 'camo',
    name: 'CAMO',
    description: 'CA Manager, CA Technical Officer and CAMO QM',
    tagline: 'Continuing Airworthiness. Always.',
    color: '#E53935',
    roles: [
      ROLES.CA_MANAGER,
      ROLES.CA_TECHNICAL_OFFICER,
      ROLES.CAMO_QM_MANAGER,
      ROLES.CAMO_QM_OFFICER
    ]
  },
  {
    slug: 'mro',
    name: 'MRO & Planning',
    description: 'MRO Manager, Engineers, MRO QM, and Planning',
    tagline: 'Maintain. Plan. Keep Flying.',
    color: '#7CB342',
    roles: [
      ROLES.MRO_MANAGER,
      ROLES.MRO_ENGINEER,
      ROLES.MRO_QM_MANAGER,
      ROLES.MRO_QM_OFFICER,
      ROLES.PLANNING_MANAGER,
      ROLES.PLANNING_OFFICER
    ]
  },
  {
    slug: 'store',
    name: 'Procurement & Store',
    description: 'Store Manager and Store / Inventory',
    tagline: 'Source. Stock. Support.',
    color: '#F5A623',
    roles: [ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE]
  },
  {
    slug: 'admin',
    name: 'Administrator',
    description: 'Roles, Analytics, Approvals, Safety, SMS, and Employee',
    tagline: '',
    color: '#F0C419',
    roles: [
      ROLES.SUPER_ADMIN,
      ROLES.SPECTATOR,
      ROLES.SAFETY_COFS,
      ROLES.SAFETY_MANAGER,
      ROLES.SAFETY_OFFICER,
      ROLES.MIS,
      ROLES.HR_MANAGER,
      ROLES.EMPLOYEE
    ]
  },
  {
    slug: 'client',
    name: 'Client & External Operator',
    description: 'Client portal and hangar operator login',
    tagline: 'Connect. Collaborate. Fly Further.',
    color: '#1A4F9C',
    roles: [ROLES.CLIENT, ROLES.EXTERNAL_OPERATOR]
  }
];

/** Legacy slug aliases → canonical department slug */
const LOGIN_DEPARTMENT_ALIASES = {
  sales: 'ops',
  planning: 'mro',
  operator: 'client'
};

export const LOGIN_DEPARTMENT_SLUGS = LOGIN_DEPARTMENTS.map((d) => d.slug);

export function getLoginDepartment(slug) {
  if (!slug) return null;
  const canonical = LOGIN_DEPARTMENT_ALIASES[slug] || slug;
  return LOGIN_DEPARTMENTS.find((d) => d.slug === canonical) || null;
}

export function isRoleAllowedForDepartment(role, slug) {
  const dept = getLoginDepartment(slug);
  if (!dept || !role) return false;
  return dept.roles.includes(role);
}

/** Login department slugs a role belongs to (usually 0–1). */
export function getDepartmentSlugsForRole(role) {
  if (!role) return [];
  return LOGIN_DEPARTMENTS
    .filter((d) => d.roles.includes(role))
    .map((d) => d.slug);
}

/** Default landing path after login for a given user/role. */
export function getPostLoginPath(userOrRole) {
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole?.role;
  if (role === ROLES.CLIENT) return '/client-portal';
  if (role === ROLES.EXTERNAL_OPERATOR) return '/operator-portal';
  return '/notice-board';
}
